import { ConfigService } from '@stockcare-services/config-service/config.service';
import { BasketStaging } from '@stockcare-storage/basket-staging/basketStaging';
import { BasketStagingStorageService } from '@stockcare-storage/basket-staging/basket-staging-storage.service';
import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, interval, Subscription } from 'rxjs';
import { switchMap, filter } from 'rxjs/operators';
import { SettingsService } from '@stockcare-services/settings-service/settings.service';
import { Settings } from '@stockcare-helpers/settings';
import { AuthenticationStateService } from '@stockcare-services/authentication/authentication-state.service';
import { ApplicationInsightsService } from '@stockcare-services/application-insights/application-insights.service';
import { DeviceService } from '@stockcare-services/devices-service/devices.service';
import { IHealthInformation } from '@stockcare-models/IHealthInformation';
import { Basket } from '@stockcare-models/Basket';

@Injectable({
  providedIn: 'root',
})
export class BasketPollingService implements OnDestroy {
  polledBasket$: Observable<number>;
  private basketPollingInterval = new Subject();
  subscriptions = new Subscription();
  isLoggedIn: boolean;
  lastRunDateTime: Date;
  lastSuccessDateTime: Date;
  lastFailureDateTime: Date;
  failureCountToday: number = 0;
  successCountToday: number = 0;
  firstRunDateTime: Date;
  pendingBasketsCount: number = 0;

  constructor(
    private http: HttpClient,
    private basketStaging: BasketStagingStorageService,
    private conf: ConfigService,
    private settingsService: SettingsService,
    private authenticationStateService: AuthenticationStateService,
    private applicationInsightsService: ApplicationInsightsService,
    private deviceService: DeviceService
  ) {}

  initialize() {
    console.log('Starting basket polling service');
    this.startPolling();
    this.getSettings();
    this.initializeSubscriptions();
    this.firstRunDateTime = new Date();
  }

  initializeSubscriptions() {
    this.subscriptions.add(
      this.settingsService.settingsUpdated
        .pipe(filter((settings) => this.settingsService.hasSettingBeenUpdated(Settings.BASKET_RETRY_INTERVAL, settings)))
        .subscribe(() => {
          this.getSettings();
        })
    );

    this.subscriptions.add(
      this.authenticationStateService.isLoggedIn().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      })
    );
  }

  checkHealth(): IHealthInformation {
    return {
      name: 'Basket Polling Service',
      firstRunDateTime: this.firstRunDateTime,
      lastRunDateTime: this.lastRunDateTime,
      lastSuccessDateTime: this.lastSuccessDateTime,
      lastFailureDateTime: this.lastFailureDateTime,
      successCountToday: this.successCountToday,
      failureCountToday: this.failureCountToday,
      isHealthy: this.pendingBasketsCount === 0,
    };
  }

  private startPolling() {
    this.subscriptions.add(
      this.basketPollingInterval.pipe(switchMap((pollingInterval: number) => interval(pollingInterval))).subscribe(() => this.pollBaskets())
    );
  }

  private pollBaskets() {
    if (this.lastRunDateTime && this.lastRunDateTime.toDateString() !== new Date().toDateString()) {
      this.successCountToday = 0;
      this.failureCountToday = 0;
    }

    this.lastRunDateTime = new Date();

    this.getBaskets().then((baskets) => {
      if (baskets == null || baskets.length == 0) {
        this.pendingBasketsCount = 0;
        return;
      }

      this.pendingBasketsCount = baskets.length;

      for (const basket of baskets) {
        this.postBasket(basket).subscribe(
          (result) => {
            this.updateBasket(basket.clientId);
          },
          () => {
            this.removeExpiredBasket(basket);
          }
        );
      }
    });

    this.removeExpiredBaskets();
  }

  private postBasket(basket: Basket): Observable<any> {
    const device = this.deviceService.getDeviceFromLocalStorage();

    return new Observable((observer) => {
      this.http.post<any>(`${this.conf.config.baseUrl}/baskets`, basket).subscribe(
        (result) => {

          if(!result) {
            observer.next(result);
            return;
          }

          var result = result;

          this.applicationInsightsService.logEvent('basket-submit-success', {
            id: result.queueId,
            deviceId: device.id,
            institutionId: device.institutionId,
            institutionName: device.institutionName,
            physicalLocationId: device.physicalLocationId,
            physicalLocationName: device.physicalLocationName,
            basketClientId: result.clientId,
            basketQueueId: result.queueId,
          });

          this.successCountToday++;
          this.lastSuccessDateTime = new Date();

          observer.next(result);
        },
        (error) => {
          this.failureCountToday++;
          this.lastFailureDateTime = new Date();

          this.applicationInsightsService.logEvent('basket-submit-failed', {
            deviceId: device.id,
            institutionId: device.institutionId,
            institutionName: device.institutionName,
            physicalLocationId: device.physicalLocationId,
            physicalLocationName: device.physicalLocationName,
            basketClientId: basket.clientId,
            basketQueueId: basket.queueId,
            error: error,
          });

          observer.error(error);
        }
      );
    });
  }

  private async removeExpiredBaskets() {
    const expiredBaskets = await this.getExpiredBaskets();
    for (const basket of expiredBaskets) {
      this.basketStaging.deleteStagedBasket(basket);
    }
  }

  private async getExpiredBaskets(): Promise<BasketStaging[]> {
    return (await this.basketStaging.getStagedBaskets()).filter((x) => x.Processed && new Date() > x.ExpiryDate);
  }

  private async getBaskets(): Promise<Basket[]> {
    var allBaskets = await this.basketStaging.getStagedBaskets();
    var unprocessedBaskets = allBaskets.filter((x) => x.Processed === false).map((x) => JSON.parse(x.BasketJson));

    return unprocessedBaskets;
  }

  private getSettings() {
    const basketPollingInterval = this.settingsService.getSettingValue(Settings.BASKET_RETRY_INTERVAL) * 1000;

    this.basketPollingInterval.next(basketPollingInterval ?? 3000);
  }

  private async updateBasket(clientId) {
    const basket = await this.basketStaging.getClientBasket(clientId);
    await this.basketStaging.deleteStagedBasket(basket);
  }

  private async updateBasketExpiryDate(result) {
    var expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 2);

    const basket = await this.basketStaging.getClientBasket(result.clientId);
    basket.ExpiryDate = expiryDate;
    await this.basketStaging.updateBasket(basket);
  }

  private async removeExpiredBasket(basket) {
    const clientBasket = await this.basketStaging.getClientBasket(basket.clientId);
    if (!clientBasket.ExpiryDate) {
      await this.updateBasketExpiryDate(basket);
    } else {
      var today = new Date();
      var expiryDate = clientBasket.ExpiryDate;
      expiryDate.setDate(clientBasket.ExpiryDate.getDate() + 7);

      if (expiryDate < today) {
        await this.updateBasket(basket.clientId);
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
